import orderBy from 'lodash.orderby'
import {makeAutoObservable} from 'mobx'
import {createContext} from 'react'
import {Lang} from 'yougile-lang'
import {SearchUtil} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {createUseStore} from '@containers/hooks'
import {TableUserInfo, UsersTableState, UserTablesProps} from './types'

export class UsersTableStore {
  state: UsersTableState = {
    searchQuery: '',
    selected: new Set<DataObjectId>(),
    users: [],
    singleMode: false,
    showPosition: false,
    userElements: new Map(),
    hideDepartment: false,
    tasksOnUser: {},
    toggle: true
  }

  constructor(props: UserTablesProps) {
    makeAutoObservable(this)
    this.init(props)
    this.initialSort()
  }

  initialSort(): void {
    if (!this.state.selected.size) {
      return
    }
    this.state.users = orderBy(this.state.users, [
      u => !this.state.selected.has(u.userId),
      u => u.userName.toLowerCase()
    ])
  }

  init(props: UserTablesProps): void {
    this.state.users = orderBy(props.users, x =>
      this.state.users.findIndex(y => y.userId === x.userId)
    )
    this.state.selected = new Set(props.selected)
    this.state.singleMode = props.singleMode
    this.state.showPosition = props.showPositionColumn
    this.state.hideDepartment = props.hideDepartmentColumn
    this.state.tasksOnUser = props.tasksOnUser
  }

  setUserElement(userId: DataObjectId, element: Element): void {
    this.state.userElements.set(userId, element)
  }

  toggleSelected(userId: DataObjectId): void {
    if (this.state.selected.has(userId)) {
      this.state.selected.delete(userId)
    } else {
      if (this.state.singleMode) {
        this.state.selected.clear()
      }
      this.state.selected.add(userId)
    }
  }

  get allSelected(): boolean | undefined {
    const filteredSelectedUsers = this.filteredUsers.filter(x =>
      this.state.selected.has(x.userId)
    )
    if (!filteredSelectedUsers.length) {
      return false
    }
    if (filteredSelectedUsers.length === this.filteredUsers.length) {
      return true
    }
    return undefined
  }

  setAllSelected(allSelected: boolean): DataObjectId[] {
    if (allSelected) {
      return this.selectAll()
    } else {
      return this.unselectAll()
    }
  }

  selectAll(): DataObjectId[] {
    const selectUserIds = this.filteredUsers
      .filter(x => !this.state.selected.has(x.userId))
      .map(x => x.userId)
    selectUserIds.forEach(id => this.state.selected.add(id))
    return selectUserIds
  }

  unselectAll(): DataObjectId[] {
    const unselectUserIds = this.filteredUsers
      .filter(x => this.state.selected.has(x.userId))
      .map(x => x.userId)
    unselectUserIds.forEach(id => this.state.selected.delete(id))
    return unselectUserIds
  }

  setSearchQuery(v: string): void {
    this.state.searchQuery = v
  }

  get searchPlaceholder(): string {
    if (this.state.hideDepartment) {
      return Lang.msg.component.usersTable.searchWithoutDepartment
    }
    return this.state.showPosition
      ? Lang.msg.component.usersTable.searchWithPosition
      : Lang.msg.component.usersTable.search
  }

  get filteredUsers(): TableUserInfo[] {
    return this.state.users.filter(x =>
      SearchUtil.smartSearch(
        [
          x.userName,
          !this.state.hideDepartment && x.departments,
          this.state.showPosition && x.position,
          x.role
        ]
          .filter(Boolean)
          .join(' '),
        this.state.searchQuery
      )
    )
  }

  toggleTurnOff(): void {
    this.state.toggle = false
  }
}

const PropsContext = createContext(undefined as unknown as UserTablesProps)
const [UsersTableContext, useUsersTableStore] = createUseStore(UsersTableStore)
export {UsersTableContext, useUsersTableStore, PropsContext}
